import React, { useEffect, useState } from "react";
import { AdsClick, Cached, LockOutlined, QrCode } from '@mui/icons-material';
import { Button, Grid, Modal, Tooltip } from "@mui/material";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { useMediaQuery, useTheme } from "@mui/material";

// Components
import TableModel from "../../components/atoms/tables/TableModel";
import { BackdropLoading } from "../../components/BackdropLoading";
import { SearchBar } from "../../components/atoms/searchBars/SearchBar";

// Services
import { AssetApi } from "../../services/AssetApi";

// Utils
import { CheckResponse } from "../../utils/checkResponse";

// Styles
import { BoxModal, CustomButton, CustomIconButton, getStatusLabel, PageTitle, TableWrapperOverrides } from "./styles";
import { Container, ContainerBarSearch, ContainerButtonDownload, ContainersButtonsGroup, CustomButtonV2, SectionPageLeft, SectionPageTitle } from "../styles";
import { QrModalCard } from "../../components/atoms/modalCards/QrModalCard";
import { getIcon } from "../../utils/iconConfig";
import { ColorsContext } from "../../Context/ColorsContext";
import { AssetWorkflowsModalCard } from "../../components/atoms/modalCards/AssetWorkflowsModalCard";
import { getQrType } from "../../utils/commonFunctions";


export const assignQR = (row, title, handleViewQr) => (
    <Tooltip
        title={title}
    >
        <span>
            <CustomIconButton
                size="small"
                onClick={() => handleViewQr(row)}
            >
                <QrCode />
            </CustomIconButton>
        </span>
    </Tooltip>
)

export const viewWorkflows = (row, title, handleViewWorkflows) => (
    <Tooltip
        title={title}
    >
        {row.isLocked && (
            <span>
                <CustomIconButton
                    size="small"
                    onClick={() => handleViewWorkflows(row)}
                >
                    <LockOutlined />
                </CustomIconButton>
            </span>
            )
        }
    </Tooltip>
)

export const AssetList = (props) => {
    const { t } = useTranslation();
    const { colors } = React.useContext(ColorsContext);
    const location = useLocation();

    const assetApi = new AssetApi();
    const {check} = new CheckResponse();

    const [headers, setHeaders] = useState([]);
    const [filteredRequest, setFilteredRequest] = useState([]);
    const [rows, setRows] = useState([]);
    const [loadingData, setLoadingData] = useState(false);
    
    const theme = useTheme();
	const isMobileOrSmaller = useMediaQuery(theme.breakpoints.down("sm"));
    const isDesktop = useMediaQuery(theme.breakpoints.up("lg"));

    // Visualización QR
    const [qrOpen, setQrOpen] = useState(false);
    const [qrUrl, setQrUrl] = useState(null);
    const [dataQR, setDataQR] = useState({ title: '', type: '', code: '' })
    // const [qrLoading, setQrLoading] = useState(false);

    const [workflowOpen, setWorkflowOpen] = useState(false);
    const [dataWorkflows, setDataWorkflows] = useState([])


    const getRandomInt = (min, max) => Math.floor(Math.random() * (max - min + 1)) + min;

    const shuffle = (array) => {
        const arr = [...array];
        for (let i = arr.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [arr[i], arr[j]] = [arr[j], arr[i]];
        }
        return arr;
    };

    async function loadAssets() {
        setLoadingData(true)
        const response = await assetApi.list()
        let result = check(response)
            
        if (result.status) {
            setRows(response.data)
        }
        else {
            toast.error(() => <>{result.errors}</>)
        }
        setLoadingData(false)
    }

    const loadDemoAssets = async () => {
        try {
            const response = await fetch("/assets-demo.json");

            // Archivo no existe / error HTTP
            if (!response.ok) {
                throw new Error("No se pudo cargar assets-demo.json");
            }

            const data = await response.json();

            // No es array o está vacío
            if (!Array.isArray(data) || data.length === 0) {
                throw new Error("assets-demo.json no contiene datos válidos");
            }

            return data;

        } catch (error) {
            console.error(error);
            toast.error(t('asset.syncFailed'));
            return null;
        }
    };


    const syncAssets = async () => {
        try {
            setLoadingData(true)
            // Cargar demo.json
            const data = await loadDemoAssets();
            if (!data) {
                setLoadingData(false)
                return;
            }

            // assets ya existentes
            const existingSapCodes = new Set(
                rows.map(a => String(a.sapCode))
            );
            
            // Filtramos solo aquellos que no coincidan con los sapCodes ya conocidos
            const newAssets = data.filter(item => {
                const sap = String(item.sapCode ?? item.equipo);
                return !existingSapCodes.has(sap);
            });

            // Ya no existen más assets del archivo demo
            if (newAssets.length === 0) {
                toast.info(t('asset.syncEmpty'));
                setLoadingData(false);
                return;
            }

            const limitSync = getRandomInt(8, 22);
            const dataSelected = shuffle(newAssets).slice(0, limitSync);

            // Mapear al DTO del backend
            const payload = dataSelected.map(item => ({
                sapCode: item.sapCode ?? item.equipo,
                assetDescription: item.assetDescription ?? item.descripcion_equipo,
                profileDescription: item.profileDescription ?? item.descripcion_perfil ?? "",
                location: item.location ?? item.ubicacion_tecnica,
                type: item.type ?? item.tipo,
                siteCode: item.siteCode ?? item.emplazamiento,
                siteDescription: item.siteDescription ?? item.descripcion_emplazamiento,
                criticality: item.criticality ?? item.criticidad,
                criticalityDescription: item.criticalityDescription ?? item.descripcion_criticidad,
                priority: item.priority ?? item.funcion_prioridad,
                priorityDescription: item.priorityDescription ?? item.descripcion_función_prioridad,
                PTR: item.PTR ?? item.descripcion_ptr ?? "",
                maintenanceStrategy: item.maintenanceStrategy ?? item.estrategia_mtto ?? "",
            }));

            // Enviar al endpoint
            const createResponse = await assetApi.create(payload);
            const result = check(createResponse);

            if (!result.status) {
                toast.error(() => <>{result.errors}</>);
                return;
            }

            // Volver a consultar la lista
            await loadAssets();

            toast.success(t('asset.syncSuccess'));

        } catch (error) {
            console.error(error);
            toast.error(t('asset.syncFailed'));
        }
        setLoadingData(false)
    };

    const validityTooltip = (assetStatus) => {
        let validity = `${t('asset.unlocked')}`;

        try {
            if (assetStatus) {
                validity = `${t('asset.locked')}`;
            }
        } catch (error) {
            console.log('ERROR', error)
        }

        return validity
    }

    const returnHeaders = () => {
        let heads = [
            {
                id: 'id',
                label: 'asset.table.id',
                center: true,
                disablePadding: true,
                sortArrow: true,
                isRender: true,
                sticky: true,
                direction: 'left',
            },
            {
                id: 'sapCode',
                label: 'asset.table.code',
                center: false,
                disablePadding: true,
                sortArrow: true,
                isRender: true,
            },
            {
                id: 'assetDescription',
                label: 'asset.table.equipment',
                center: false,
                disablePadding: true,
                sortArrow: true,
                isRender: true,
            },
            {
                id: 'location',
                label: 'asset.table.location',
                center: false,
                disablePadding: true,
                sortArrow: true,
                isRender: true,
            },
            {
                id: 'plant',
                label: 'asset.table.site',
                center: true,
                disablePadding: true,
                sortArrow: true,
                format: (value) => value.plant?.name,
                customSort: "plant?.name",
            },
            {
                id: 'criticalityDescription',
                label: 'asset.table.criticality',
                center: false,
                disablePadding: true,
                sortArrow: true,
                isRender: true,
            },
            {
                id: 'priorityDescription',
                label: 'asset.table.priority',
                center: false,
                disablePadding: true,
                sortArrow: true,
                isRender: true,
            },
            {
                id: 'isLocked',
                label: 'asset.table.status',
                center: false,
                disablePadding: true,
                sortArrow: true,
                // format: (value) => value.isLocked ? t('asset.active') : t('asset.inactive'),
                // format: (value) => getStatusLabel(value.isLocked, value.isLocked ? t('asset.unlocked') : t('asset.locked')),
                format: (value) => getIcon(value.isLocked ? {color: colors.darkOrange, icon: 'lock'} : { color: colors.green, icon: 'lock_open' }),
                needTooltip: (value) => validityTooltip(value.isLocked)
            },
            {
                id: 'plotPlan',
                label: 'asset.table.plotPlan',
                center: false,
                disablePadding: true,
                sortArrow: true,
                isRender: true,
            },
        ]
        let newActions = [];
        newActions.push('custom');
        newActions.push('custom2');

        if (newActions.length > 0) {
            heads.push(addActionsRow(newActions))
        }

        setHeaders(heads);
    }

    function addActionsRow(actions) {
        return {
            id: "actions",
            label: "common.actions.title",
            type: "actions",
            center: false,
            sortArrow: false,
            actionsFormat: (value, key) => {
                if (key === "custom") return assignQR(value, t('asset.table.tooltip.viewQR'), handleViewQr)
                else if (key === "custom2") return viewWorkflows(value, t('asset.table.tooltip.viewBlocked'), handleViewWorkflows)
            },
            sticky: true,
            direction: 'right',
            keys: actions,
        }
    }

    const handleViewQr = async (rowAsset) => {
        try {
            setLoadingData(true);
            let assetId = rowAsset.id;
            setDataQR({
                title: rowAsset.assetDescription,
                type: getQrType(rowAsset.type),
                code: rowAsset.sapCode
            })

            const response = await assetApi.downloadQR([assetId], []);

            if (!response || response.status !== 200) {
                toast.error(t('asset.qrFailed'));
                return;
            }

            const blob = new Blob([response.data], { type: 'image/png' });
            const url = URL.createObjectURL(blob);

            setQrUrl(url);
            setQrOpen(true);

        } catch (err) {
            console.error(err);
            toast.error(t('asset.qrFailed'));
        } finally {
            setLoadingData(false);
        }
    };

    const handleViewWorkflows = async (rowAsset) => {
        try {
            setLoadingData(true);
            setDataWorkflows(rowAsset.workflowDatas)
            setWorkflowOpen(true);

        } catch (err) {
            console.error(err);
            toast.error(t('asset.workflowDataFailed'));
        } finally {
            setLoadingData(false);
        }
    };

    const handleCloseQr = () => {
        if (qrUrl) URL.revokeObjectURL(qrUrl);
        setQrUrl(null);
        setQrOpen(false);
    };

    const handleCloseWorkflow = () => {
        setDataWorkflows([])
        setWorkflowOpen(false);
    };

    useEffect(() => {
        returnHeaders()

        async function call() {
            await loadAssets()
        }
        call()
    }, [])

    return (
        <Container open={props.open} style={{ marginBottom: 0 }}>
            <Grid container 
              spacing={1}
              alignItems="center"
              sx={{ mb: { md: 2 } }}
              columns={{ xs: 12, sm: 12, md: 12, lg: 13, xl: 15 }}
            >
                <Grid item xs={12} sm={12} md={12} lg={5} xl={4}>
                    <PageTitle>
                      {t('asset.title')}
                    </PageTitle>
                </Grid>
                <Grid item xs={12} sm={8} md={9} lg={5} xl={6}>
                  <ContainerBarSearch>
						<SearchBar
							rows={rows}
							setFilteredRequest={setFilteredRequest}
							filterText={isMobileOrSmaller ? t('common.search') : t('common.searchBars.asset')}
							filterKeys={['id', 'sapCode', 'assetDescription', 'location', 'e.plant?.name', 'criticalityDescription', 'priorityDescription', 'plotPlan']}
							initialValue={location.state?.filter}
                    	/>
                  </ContainerBarSearch>
                </Grid>
                {isDesktop &&
                  	<Grid item md={false} lg={1} xl={3}></Grid>
                }
                <Grid item xs={12} sm={4} md={3} lg={2} xl={2}
            		sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
					<CustomButtonV2
						variant="contained"
						btnType="primary"
						startIcon={<Cached />}
						aria-label="sync"
						onClick={syncAssets}
						disabled={loadingData}
					>
						{t('asset.buttonSync')}
					</CustomButtonV2>
                </Grid>
            </Grid>

			<TableModel
				headers={headers}
				filteredRequest={filteredRequest}
				rows={rows}
				hideFilterDate={true}
				hideToolbar={true}
				defaultRowsPerPage={25}
			/>

            <Modal open={qrOpen}>
                <BoxModal>
                    <QrModalCard
                        data={dataQR}
                        loading={loadingData}
                        qrUrl={qrUrl}
                        handleCloseQr={handleCloseQr}
                    />
                </BoxModal>
            </Modal>

            <Modal open={workflowOpen}>
                <BoxModal>
                    <AssetWorkflowsModalCard 
                        handleCloseWorkflow={handleCloseWorkflow}
                        data={dataWorkflows}
                    />
                </BoxModal>
            </Modal>

            <BackdropLoading open={loadingData} />
        </Container>
    );
};